import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { ModalService } from 'src/app/_services/modal.service';
import { OrganisationService } from 'src/app/_services/organisation.service';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { User } from 'src/app/_models/user';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { Laboratory } from 'src/app/_models/laboratory';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { OrganisationCreate } from 'src/app/_models/organisation';
import {
  LICENSE_LIMITS,
  licenseTypeOptions,
} from 'src/app/_models/license-type';

@Component({
  selector: 'app-create-organisation',
  templateUrl: './create-organisation.component.html',
  styleUrls: ['./create-organisation.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    ModalComponent,
    ToastComponent,
  ],
})
export class CreateOrganisationComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly organisationService = inject(OrganisationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authEventService = inject(AuthEventService);

  private successToast?: ReturnType<typeof setTimeout>;

  isProcessing = false;
  error: ErrorObject;
  currentUser: User;
  laboratory: Laboratory;

  // Toast
  successResponse: string = '';
  displayToast = false;

  // Make licence type enum available in the template
  licenseLimits = LICENSE_LIMITS;
  selectedLicenseTypeUsers: number;
  selectedLicenseTypeDevices: number;

  allowNavigation: Subject<boolean> = new Subject<boolean>();
  private readonly unsubscribe$ = new Subject<void>();

  orgNameField = new FormBase({
    key: 'orgName',
    label: 'Organisation Name',
    required: true,
    type: 'text',
    placeholder: 'Organisation name',
    value: '',
    options: [],
  });

  licenseField = new FormBase({
    key: 'license',
    label: 'License Type',
    required: true,
    type: 'text',
    value: '',
    placeholder: 'Select license type',
    options: licenseTypeOptions(),
  });

  superUserFirstNameField = new FormBase({
    key: 'superUserFirstName',
    label: 'First Name',
    required: true,
    type: 'text',
    placeholder: 'First name',
    value: '',
    options: [],
  });

  superUserLastNameField = new FormBase({
    key: 'superUserLastName',
    label: 'Last Name',
    required: true,
    type: 'text',
    placeholder: 'Last name',
    value: '',
    options: [],
  });

  superUserEmailField = new FormBase({
    key: 'superUserEmail',
    label: 'Email',
    required: true,
    type: 'email',
    placeholder: 'Email',
    value: '',
    options: [],
  });

  addOrgForm = this.formBuilder.group({
    [this.orgNameField.key]: [
      this.orgNameField.value,
      [Validators.required, Validators.minLength(3), Validators.maxLength(250)],
    ],
    [this.licenseField.key]: ['', Validators.required],
    [this.superUserFirstNameField.key]: ['', Validators.required],
    [this.superUserLastNameField.key]: ['', Validators.required],
    [this.superUserEmailField.key]: [
      '',
      [Validators.required, Validators.email],
    ],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });

    this.laboratory = this.authenticationService.selectedLaboratory;
    // Check for cached form on init (after login redirect)
    const unsavedCachedData =
      this.organisationService.retrieveAndClearUnsavedOrgCache;

    this.initialiseSubscriptions();

    if (unsavedCachedData) {
      this.updateFormValues(unsavedCachedData);
    }
  }

  initialiseSubscriptions(): void {
    // Subscribe to changes in the license dropdown
    this.addOrgForm
      .get(this.licenseField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((licenseType) => {
        // Handle license type change if needed
        this.selectedLicenseTypeUsers =
          this.licenseLimits[licenseType].maxUsers;
        this.selectedLicenseTypeDevices =
          this.licenseLimits[licenseType].maxDevices;
      });
  }

  openModal(modalId: string) {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string) {
    this.modalService.close(modalId);
  }

  showToast() {
    this.displayToast = true;
    this.successToast = setTimeout(() => {
      this.displayToast = false;
    }, 5000);
  }

  modalButtonClicked(buttonId: string) {
    switch (buttonId) {
      case 'close-button':
        this.closeModal('error');
        break;
      case 'cancel-button':
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  onSubmit() {
    if (this.addOrgForm.invalid) {
      return;
    }

    this.isProcessing = true;
    this.successResponse = '';

    this.organisationService
      .createOrganisation({
        organisationName: this.addOrgForm.value.orgName,
        licenseType: parseInt(this.addOrgForm.value.license),
        superUserFirstName: this.addOrgForm.value.superUserFirstName,
        superUserLastName: this.addOrgForm.value.superUserLastName,
        superUserEmail: this.addOrgForm.value.superUserEmail,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: string) => {
          this.isProcessing = false;
          this.addOrgForm.reset();
          this.addOrgForm.markAsPristine();
          this.successResponse = response;
          this.showToast();
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
          this.openModal('error');
        },
      });
  }

  updateFormValues(org: OrganisationCreate): void {
    this.addOrgForm.patchValue({
      orgName: org.organisationName,
      license: org.licenseType?.toString(),
      superUserFirstName: org.superUserFirstName,
      superUserLastName: org.superUserLastName,
      superUserEmail: org.superUserEmail,
    });

    this.addOrgForm.markAsDirty();
  }

  getCurrentFormValues(): OrganisationCreate {
    const orgCache: OrganisationCreate = {
      organisationName: this.addOrgForm.controls['orgName'].value,
      licenseType: parseInt(this.addOrgForm.controls['license'].value),
      superUserFirstName: this.addOrgForm.controls['superUserFirstName'].value,
      superUserLastName: this.addOrgForm.controls['superUserLastName'].value,
      superUserEmail: this.addOrgForm.controls['superUserEmail'].value,
    };
    return orgCache;
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    // Cache form if we're being destroyed due to 401 navigation
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.addOrgForm.pristine
    ) {
      const formValues = this.getCurrentFormValues();
      this.organisationService.onUpdateCreateOrgCache(formValues);
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.successToast) {
      clearTimeout(this.successToast);
    }
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.addOrgForm.pristine ||
        !this.currentUser ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation
          .pipe(take(1))
          .subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
